// btc15/math.js — the desk's arithmetic, mirrored for the browser so the
// tab can re-price every agent between polls from the live Coinbase tape.
// Same formulas as backend/btc15_desk.py; the server's record is the
// truth, this only fills the seconds between its ticks.

export const CHECKPOINTS = [0, 5, 10, 13];
export const P_FLOOR = 0.02;
export const P_CEIL = 0.98;
export const TAKER_FEE_RATE = 0.07;     // hit the ask: 0.07·p·(1−p) a contract
export const MAKER_FEE_RATE = 0.0175;   // rest at the bid: a quarter of that, and no spread paid
export const SHRINK_TO_MARKET = 0.75;

const erf = (x) => {
  // Abramowitz–Stegun 7.1.26, |error| < 1.5e-7 — plenty for a probability shown to the percent
  const s = x < 0 ? -1 : 1;
  const a = Math.abs(x);
  const t = 1 / (1 + 0.3275911 * a);
  const y = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-a * a);
  return s * y;
};
export const phi = (x) => 0.5 * (1 + erf(x / Math.SQRT2));
export const clip = (p) => Math.max(P_FLOOR, Math.min(P_CEIL, Number(p)));
export const logit = (p) => { const q = clip(p); return Math.log(q / (1 - q)); };
export const sigmoid = (x) => 1 / (1 + Math.exp(-Math.max(-40, Math.min(40, x))));
export const kalshiFee = (price, contracts = 1) => TAKER_FEE_RATE * price * (1 - price) * contracts;
export const makerFee = (price, contracts = 1) => MAKER_FEE_RATE * price * (1 - price) * contracts;

export function remainingVarianceFrac(minute) {
  const m = Math.max(0, Math.min(15, Number(minute) || 0));
  if (m <= 14) return ((14 - m) + 1 / 3) / 15;
  const left = 15 - m;
  return Math.max((left ** 3) / 3 / 15, 0.0005);
}

export function gapZ(price, target, minute, sigma15) {
  if (!(price > 0) || !(target > 0) || !(sigma15 > 0)) return 0;
  return Math.log(price / target) / (sigma15 * Math.sqrt(remainingVarianceFrac(minute)));
}

export const fairValueP = (price, target, minute, sigma15) => (price > 0 && target > 0 ? clip(phi(gapZ(price, target, minute, sigma15))) : 0.5);

export const checkpointFor = (minute) => CHECKPOINTS.filter((c) => c <= minute).pop() ?? 0;

export function applyCalibration(raw, marketP, params) {
  if (marketP == null) return clip(raw);
  if (!params) return clip((1 - SHRINK_TO_MARKET) * raw + SHRINK_TO_MARKET * marketP);
  return clip(sigmoid(params.a * logit(raw) + params.b * logit(marketP) + params.c));
}

// Re-price every agent from a fresh BTC print. The server's last raw
// probabilities carry each method's tilt (momentum, flow…) which the
// browser cannot recompute without the minute closes; the fair value
// moves with the live price and each tilt rides on top of it unchanged.
export function liveProbabilities({ price, target, minute, sigma15, sigmaRecent, marketP, serverRaw, calibration }) {
  if (!(price > 0) || !(target > 0) || !serverRaw) return null;
  const fvServer = serverRaw.atlas;
  const fv = fairValueP(price, target, minute, sigma15);
  const cp = String(checkpointFor(minute));
  const raw = {};
  for (const [agent, p] of Object.entries(serverRaw)) {
    if (agent === "atlas") raw[agent] = fv;
    else if (agent === "kestrel") raw[agent] = sigmaRecent ? fairValueP(price, target, minute, Math.min(Math.max(sigmaRecent, 0.5 * sigma15), 3 * sigma15)) : fv;
    else if (agent === "echo") raw[agent] = marketP != null ? clip(0.5 * fv + 0.5 * marketP) : fv;
    else raw[agent] = clip(fv + (fvServer != null ? Number(p) - fvServer : 0));
  }
  const calibrated = {};
  for (const [agent, p] of Object.entries(raw)) calibrated[agent] = applyCalibration(p, marketP, calibration?.[agent]?.params?.[cp]);
  return { raw, calibrated, checkpoint: Number(cp), fv };
}

export const markPrice = (wager, yesBid, yesAsk) => {
  if (yesBid == null || yesAsk == null) return null;
  const px = wager.side === "yes" ? Number(yesBid) : 1 - Number(yesAsk);
  return px > 0 && px < 1 ? px : null;
};

export const unrealised = (wager, mark) => (mark == null ? null : Number(wager.contracts) * mark - kalshiFee(mark, Number(wager.contracts)) - Number(wager.cost));
